import type { IdentityAnnotations } from '../lib/portal';

/**
 * Passport-authoritative identity notice (Asana 1218395560074120). Rendered on
 * Personal Information and Passport Information from SERVER state only: the
 * Hub decides whether a passport update has been applied and supplies the
 * applicant-facing copy; this component never infers it from local values.
 *
 * - `notice` is shown whenever the Hub provides one (e.g. "pre_passport":
 *   the passport step will confirm these details; "passport_applied": the
 *   passport now drives them).
 * Canonical current values are a separate read-only display. Never merge them
 * into draft/default form values: those are the applicant's source declaration,
 * and Hub DOB display strings are not date-input values.
 */
export function IdentityAnnotationsNotice({
  annotations,
  stepKind,
}: {
  annotations: IdentityAnnotations | undefined;
  stepKind: 'personal' | 'passport';
}) {
  if (!annotations) return null;
  const notice = annotations.notice?.text?.trim();
  const current = annotations.identityApplied
    ? annotations.fields.filter((f) => f.authoritativeValue !== null)
    : [];
  if (!notice && current.length === 0) return null;
  return (
    <div
      className={`globus-banner${annotations.identityApplied ? ' ok' : ''}`}
      role="status"
      data-testid={`identity-annotations-${stepKind}`}
      data-identity-applied={annotations.identityApplied ? 'true' : 'false'}
    >
      {notice ? <p style={{ margin: 0 }}>{notice}</p> : null}
      {current.length > 0 ? (
        <section aria-label="Current identity details" style={{ marginTop: notice ? 8 : 0 }}>
          <strong>Current details</strong>
          <dl style={{ margin: '8px 0 0' }}>
            {current.map((f) => (
              <div key={f.field} style={{ marginTop: 4 }}>
                <dt style={{ display: 'inline', fontWeight: 600 }}>{f.label}: </dt>
                <dd style={{ display: 'inline', margin: 0 }}>
                  {f.authoritativeValue}
                  {f.isDifferent && f.displayCopy ? <span> — {f.displayCopy}</span> : null}
                </dd>
              </div>
            ))}
          </dl>
        </section>
      ) : null}
    </div>
  );
}
